import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  TextInput,
  Button,
  Flex,
  Stack,
  Text,
  Card,
  Grid,
  Modal,
} from "@mantine/core";
import { useQuery, useMutation } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import EventList from "./EventList";
import EntityActionProvider from "./EntityActionProvider";
import EntityActionLibrary from "./EntityActionLibrary";
import initializeMetaPixel from "@util/initializeMetaPixel";
import initializeGtm from "@util/initializeGtm";

export default function EntityActionAssociator({ effortId }) {
  const { t } = useTranslation();
  const [info, setInfo] = useState({
    meta_pixel_id: "",
    gtm_id: "",
    events: [],
  });
  const [modalWarning, setModalWarning] = useState(null);

  const queryKey = `entityActionAssociator${effortId}`;

  // useEffect(() => {
  //   initializeMetaPixel("2120270034897755").then(() => {
  //     console.log("Meta Pixel initialized");
  //   });
  //   initializeGtm("AW-1017383762").then(() => {
  //     console.log("GTM initialized");
  //   });
  // }, []);

  const { data, isLoading } = useQuery({
    queryKey: [queryKey],
    queryFn: async () => {
      const { data } = await axios.post(
        `/paid-media/get-paid-media-settings/`,
        {
          campaign_effort_id: effortId,
        }
      );
      setInfo({
        ...info,
        ...data.response[0],
      });
      return data.response[0];
    },
  });

  const {
    mutate: updatePaidMediaSettings,
    isPending: isUpdatingPaidMediaSettings,
  } = useMutation({
    mutationFn: async (newInfo) => {
      const { data } = await axios.post(
        `/paid-media/update-paid-media-settings/`,
        {
          campaign_effort_id: effortId,
          paid_media_settings: newInfo,
        }
      );
      return data.response[0];
    },
    onSuccess: () => {
      // true
    },
    onError: (error) => {
      toast.error(error);
    },
  });

  function onItemSubmit(item) {
    if (!item.provider) {
      return toast.error(
        t("mixer:entity_action_associator:missing_provider_error")
      );
    }
    if (item.provider === "meta_pixel" && !info.meta_pixel_id) {
      return toast.error(
        t("mixer:entity_action_associator:meta_pixel_id_required_error")
      );
    }
    if (item.provider === "gtm" && !info.gtm_id) {
      return toast.error(
        t("mixer:entity_action_associator:gtm_id_required_error")
      );
    }
    const newInfo = { ...info, events: [...info.events, item] };
    setInfo(newInfo);
    updatePaidMediaSettings(newInfo);
  }

  function onProviderSubmit(provider, newId) {
    const providerEvents = info.events.filter(
      (event) => matchEventProviderToConfigProvider(event.provider) === provider
    );
    if (!providerEvents.length) {
      const newInfo = { ...info, [provider]: newId };
      setInfo({ ...info, [provider]: newId });
      updatePaidMediaSettings(newInfo);
    } else {
      setModalWarning({
        events: providerEvents,
        new_id: newId,
        old_id: info[provider],
        provider: provider,
      });
    }
  }

  function onModalConfirm() {
    if (!modalWarning.new_id) {
      const newInfo = {
        ...info,
        [modalWarning.provider]: "",
        events: info.events.filter(
          (event) =>
            matchEventProviderToConfigProvider(event.provider) !==
            modalWarning.provider
        ),
      };
      setInfo(newInfo);
      updatePaidMediaSettings(newInfo);
    } else {
      const newInfo = { ...info, [modalWarning.provider]: modalWarning.new_id };
      setInfo(newInfo);
      updatePaidMediaSettings(newInfo);
    }
    setModalWarning(null);
  }

  function onEventsChange(events) {
    const newInfo = { ...info, events };
    setInfo(newInfo);
    updatePaidMediaSettings(newInfo);
  }

  const hasOneId = info.meta_pixel_id || info.gtm_id ? true : false;

  return (
    <div>
      <ModalWarning
        warning={modalWarning}
        onConfirm={() => {
          onModalConfirm();
        }}
        onCancel={() => {
          setModalWarning(null);
        }}
      />
      <Stack gap="xs">
        <Grid>
          <Grid.Col
            span={{
              base: 12,
              md: 6,
            }}
          >
            <EntityActionProvider
              providerInfo={{
                title: "Meta Pixel Configuration",
                id: info.meta_pixel_id,
                label: "Meta Pixel ID",
                provider: "meta_pixel",
                provider_name: "Meta Pixel",
              }}
              onSubmit={(newId) => onProviderSubmit("meta_pixel_id", newId)}
              onAddEvent={(item) => onItemSubmit(item)}
            />
          </Grid.Col>
          <Grid.Col
            span={{
              base: 12,
              md: 6,
            }}
          >
            <EntityActionProvider
              providerInfo={{
                title: "Google Tag Manager Configuration",
                id: info.gtm_id,
                label: "Google Tag Manager ID",
                provider: "gtm",
                provider_name: "Google Tag Manager",
              }}
              onSubmit={(newId) => onProviderSubmit("gtm_id", newId)}
              onAddEvent={(item) => onItemSubmit(item)}
            />
          </Grid.Col>
        </Grid>
        {hasOneId && (
          <Card>
            <EventList
              events={info.events}
              setEvents={(events) => onEventsChange(events)}
            />
          </Card>
        )}
        {hasOneId && (
          <EntityActionLibrary
            onItemSubmit={onItemSubmit}
            events={info.events}
            setEvents={(events) => onEventsChange(events)}
          />
        )}
      </Stack>
    </div>
  );
}

const ModalWarning = ({
  warning,
  onConfirm = () => {},
  onCancel = () => {},
}) => {
  const { t } = useTranslation();
  if (!warning) return null;
  const providerName =
    warning.provider === "meta_pixel_id" ? "Meta Pixel" : "Google Tag Manager";
  const isRemoving = !warning.new_id;

  return (
    <Modal
      opened={!!warning}
      onClose={() => {
        onCancel();
      }}
    >
      {/* <Text>{warning}</Text> */}
      <Stack gap="xs">
        <Text align="center" fw={600}>
          {providerName}
        </Text>
        <Text align="center">
          {!warning.new_id
            ? t("mixer:entity_action_associator:removing_provider_warning")
            : t("mixer:entity_action_associator:updating_provider_warning", {
                old_id: warning.old_id,
                new_id: warning.new_id,
              })}
        </Text>
        <Text align="center">
          {t("mixer:entity_action_associator:events_count_warning", {
            action: isRemoving
              ? t("common:remove")?.toLocaleLowerCase()
              : t("common:update")?.toLocaleLowerCase(),
            count: warning.events.length,
            event_unit:
              warning.events.length === 1
                ? t("common:event_singular")?.toLocaleLowerCase()
                : t("common:event_plural")?.toLocaleLowerCase(),
            provider: providerName,
          })}
        </Text>
        <Text align="center" fw={600}>
          {t("mixer:entity_action_associator:warning_confirm_title")}
        </Text>
        <Button
          color="green"
          onClick={() => {
            onConfirm();
          }}
        >
          {t("common:yes")}
        </Button>
        <Button
          color="red"
          onClick={() => {
            onCancel();
          }}
        >
          {t("common:no")}
        </Button>
      </Stack>
    </Modal>
  );
};

const matchEventProviderToConfigProvider = (provider) => {
  return provider === "meta_pixel" ? "meta_pixel_id" : "gtm_id";
};
